import React from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PARTNER_CITIES } from '../../content';
import AnalyticsChartPlaceholder from '../components/AnalyticsChartPlaceholder';
import AnalyticsKpiGrid from '../components/AnalyticsKpiGrid';
import PartnerSubscriptionMenu from '../components/PartnerSubscriptionMenu';
import { PortalButton } from '../components/PortalUi';
import { portalCopy } from '../copy';
import { usePartnerBusiness } from '../context/PartnerBusinessContext';
import { getMockAnalytics } from '../mocks/mockAnalytics';
import { partnerBusinessPath, partnerEditProfilePath, partnerPanelPath, partnerWizardPath } from '../partnerPaths';
import { isBusinessClassComplete } from '../utils/businessClasses';
import { isSubscriptionCancelled } from '../utils/subscription';

const PartnerBusinessPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const { business, isLoading, cancelSubscription, clearBusiness, updateDraft } = usePartnerBusiness();
  const location = useLocation();
  const navigate = useNavigate();
  const profileSaved = Boolean((location.state as { profileSaved?: boolean } | null)?.profileSaved);
  const subscriptionCancelled = Boolean(
    (location.state as { subscriptionCancelled?: boolean } | null)?.subscriptionCancelled,
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center font-jakarta text-[#5a3158]">
        Cargando…
      </div>
    );
  }

  if (!business) {
    return <Navigate to={partnerPanelPath()} replace />;
  }

  if (businessId && business.id !== businessId) {
    return <Navigate to={partnerPanelPath()} replace />;
  }

  if (business.status !== 'active' && business.status !== 'expired') {
    return <Navigate to={partnerWizardPath()} replace />;
  }

  const isExpired = business.status === 'expired';

  const handleCancelSubscription = () => {
    if (window.confirm(portalCopy.dashboard.cancelConfirm)) {
      cancelSubscription();
      navigate(partnerBusinessPath(business.id), { replace: true, state: { subscriptionCancelled: true } });
    }
  };

  const handleRenew = () => {
    updateDraft({ wizardStep: 4 });
    navigate(partnerWizardPath());
  };

  const handleDelete = () => {
    if (window.confirm(portalCopy.dashboard.deleteConfirm)) {
      clearBusiness();
      navigate(partnerPanelPath());
    }
  };

  const analytics = getMockAnalytics(business.id);
  const cityLabel = PARTNER_CITIES.find((c) => c.id === business.cityId)?.label ?? business.cityId;

  return (
    <div className="space-y-8">
      <div>
        <Link
          to={partnerPanelPath()}
          className="inline-flex font-jakarta text-sm font-semibold text-chisme-primary hover:underline"
        >
          ← {portalCopy.business.back}
        </Link>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-instrument text-3xl italic tracking-[-0.01em] text-[#2a0f2a]">
              {business.name}
            </h1>
            <span className="rounded-full bg-chisme-primary/15 px-2.5 py-0.5 font-jakarta text-xs font-bold text-chisme-primary">
              {isExpired
                ? portalCopy.dashboard.status.expired
                : isSubscriptionCancelled(business)
                  ? portalCopy.dashboard.status.cancelled
                  : portalCopy.business.active}
            </span>
          </div>
          {!isExpired && (
            <PartnerSubscriptionMenu business={business} onCancel={handleCancelSubscription} />
          )}
        </div>
        <p className="mt-1 font-jakarta text-sm text-[#5a3158]">
          {business.neighbourhood} · {cityLabel} · {business.address}
        </p>
      </div>

      {(profileSaved || subscriptionCancelled) && (
        <p
          className="rounded-2xl border border-chisme-primary/20 bg-[#fcf7fa] px-4 py-3 font-jakarta text-sm text-[#2a0f2a]"
          role="status"
        >
          {subscriptionCancelled
            ? portalCopy.business.subscriptionCancelled
            : portalCopy.business.profileSaved}
        </p>
      )}

      {isExpired && (
        <div className="rounded-[24px] border border-[rgba(74,33,71,0.1)] bg-white p-6">
          <p className="font-jakarta text-sm text-[#5a3158]">{portalCopy.dashboard.subscriptionExpired}</p>
          <PortalButton className="mt-4" onClick={handleRenew}>
            {portalCopy.dashboard.renewSubscription}
          </PortalButton>
        </div>
      )}

      <section className="space-y-6">
        <AnalyticsKpiGrid analytics={analytics} />
        <AnalyticsChartPlaceholder analytics={analytics} />
      </section>

      <section className="rounded-[24px] border border-[rgba(74,33,71,0.1)] bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-instrument text-2xl italic text-[#2a0f2a]">{portalCopy.business.profileTitle}</h2>
          <PortalButton
            variant="secondary"
            onClick={() => navigate(partnerEditProfilePath(business.id))}
          >
            {portalCopy.business.editProfile}
          </PortalButton>
        </div>
        <p className="mt-4 font-jakarta text-[15px] leading-relaxed text-[#5a3158]">{business.description}</p>
        <dl className="mt-4 grid gap-3 font-jakarta text-sm sm:grid-cols-2">
          {business.website && (
            <div>
              <dt className="text-[#5a3158]">Web</dt>
              <dd className="font-medium text-[#2a0f2a]">{business.website}</dd>
            </div>
          )}
          {business.instagram && (
            <div>
              <dt className="text-[#5a3158]">Instagram</dt>
              <dd className="font-medium text-[#2a0f2a]">{business.instagram}</dd>
            </div>
          )}
        </dl>
        {business.category === 'wellness' && business.classes.some(isBusinessClassComplete) && (
          <div className="mt-5 border-t border-[rgba(74,33,71,0.08)] pt-5">
            <h3 className="font-jakarta text-sm font-semibold text-[#2a0f2a]">
              {portalCopy.preview.classesTitle}
            </h3>
            <ul className="mt-2 space-y-1.5 font-jakarta text-sm text-[#5a3158]">
              {business.classes.filter(isBusinessClassComplete).map((cls) => (
                <li key={cls.id}>{cls.name.trim()}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <div className="border-t border-[rgba(74,33,71,0.08)] pt-6">
        <PortalButton variant="ghost" className="!text-[#5a3158]" onClick={handleDelete}>
          {portalCopy.dashboard.deleteBusiness}
        </PortalButton>
      </div>
    </div>
  );
};

export default PartnerBusinessPage;
